/**
 * Rock painting ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_paint_crafts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const rockPaintingIdeasPack: ToilePack = {
    slug: 'rock-painting-ideas',
    category: 'craft',
    name: 'Rock painting ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'ladybug', label: 'Ladybugs', subject: 'smooth rocks painted as ladybugs in a garden bed'},
        {key: 'mandala', label: 'Mandala dots', subject: 'rocks painted with dot mandalas'},
        {key: 'cactus', label: 'Cactus', subject: 'rocks painted as cacti in a pot'},
    ],
    styles: [
        {key: 'garden', label: 'In the garden', style: 'set out in the garden among moss, pebbles and low green leaves on a sunny morning', palette: 'Fresh greens and grey stone around the painted colours.', framing: 'Shot low at ground level, the rocks in the foreground and the garden softly blurred behind.'},
        {key: 'table', label: 'Craft table', style: 'drying on a wooden craft table beside small paint pots, dotting tools and a jar of brushes', palette: 'Pale wood and paint-pot colours, cheerful and bright.', framing: 'Shot from directly above, the rocks grouped in the centre of the table.'},
        {key: 'closeup', label: 'Close-up', style: 'a close-up of the finished rocks showing each dot, stroke and white highlight', palette: 'Saturated paint colours with crisp white details against the natural stone.', framing: 'A tight crop on the rocks, the background falling softly out of focus.'},
    ],
    directions: ['kindness words', 'owls', 'Halloween'],
    directionHint: 'try: kindness words',
    seo: {
        title: 'Rock painting ideas: ladybugs, mandalas, cacti',
        metaDescription: 'Nine rock painting ideas, painted ladybugs, dot mandalas and cacti, shown in the garden, on a craft table and up close.',
        h1: 'Rock painting ideas: ladybugs, mandala dots, cacti',
        tagline: 'Three classic designs, each set out in the garden, drying on a craft table and shown close up.',
        intro: 'Painted rocks are small, bright and simple to make with acrylic paint. These nine show three designs for a family to copy: smooth stones painted as ladybugs, rocks with dot mandalas and rocks painted as cacti. Each design is set out in a garden, left to dry on a craft table, and shown up close. Type a theme, such as kindness words or owls, to ask for it.',
        useCases: [
            'a decoration for a garden bed or path',
            'a kindness rock trail for children',
            'a craft activity for a rainy afternoon',
            'a small gift for a teacher or neighbour',
        ],
        faq: [
            {q: 'What do the nine pictures show?', a: 'Three rock designs: ladybugs, dot mandalas and cacti. Each is shown in a garden, on a craft table and in close-up.'},
            {q: 'Are these photos of real rocks?', a: "No. They are generated pictures of each design, not photographs of a particular family's rocks."},
            {q: 'Can I ask for a different theme?', a: 'Yes. Type a theme such as kindness words, owls or Halloween. A typed theme is a request, not a promised result.'},
        ],
    },
}
